import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, FileText, ClipboardList, ShoppingCart, Receipt, RotateCcw, Search, Download, Mail, ArrowRight, Pencil, Check, ChevronsUpDown, Archive, Eye, X, Banknote, Calendar, Copy, ChevronLeft, ChevronRight, Printer, MoreHorizontal, CheckSquare, History, ArrowUp, ArrowDown, Paperclip, Stamp } from "lucide-react";
import { documentAPI, clientAPI, productAPI, pdfAPI, convertAPI, adminAPI, exportAPI } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { BulkActionBar, useSelection } from "@/components/BulkActionBar";
import PaymentDialog from "@/components/PaymentDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import DocumentFormDialog from "@/components/DocumentFormDialog";
import { EmailSendDialog } from "@/components/EmailSendDialog";
import { InvoicePreviewSendDialog } from "@/components/billing/InvoicePreviewSendDialog";
import { AcompteDialog } from "@/components/billing/AcompteDialog";
import { SendHistoryDialog } from "@/components/SendHistoryDialog";
import { PeriodSelector } from "@/components/documents/PeriodSelector";
import { PeriodTotals } from "@/components/documents/PeriodTotals";
import { StatusLegend } from "@/components/documents/StatusLegend";
import { AvoirPendingNotes } from "@/components/documents/AvoirNotes";
import { ResteDuCell } from "@/components/documents/ResteDuCell";
import { DevisMismatchWarning } from "@/components/documents/DevisMismatchWarning";
import { PaginationBar } from "@/components/documents/PaginationBar";
import { DueDateDialog, ProformaChoiceDialog } from "@/components/documents/DocumentDialogs";
import { ClientCombobox, InlineClientDialog } from "@/components/DocumentHelpers";
import { matchesStatusFilter } from "@/lib/documentStatus";
import { ScrollableTable } from "@/components/ScrollableTable";
import { invoiceRecapText, invoiceRestePayer, appendRecapToTemplate } from "@/lib/emailRecap";
import { billedTtc, acompteDeductedTtc, devisMismatch } from "@/lib/documentAmounts";

const ALL_DOC_TYPES = [
  { value: "devis", label: "Devis", icon: FileText, workflows: ["devis_facture"] },
  { value: "proforma", label: "Devis", icon: FileText, workflows: ["proforma_commande_facture"] },
  { value: "commande", label: "Commandes", icon: ShoppingCart, workflows: ["proforma_commande_facture"] },
  { value: "facture", label: "Factures", icon: Receipt, workflows: ["devis_facture", "proforma_commande_facture"] },
  { value: "avoir", label: "Avoirs", icon: RotateCcw, workflows: ["devis_facture", "proforma_commande_facture"] },
  { value: "contrat", label: "Contrats", icon: ClipboardList, workflows: ["proforma_commande_facture"] },
];

const DOC_TYPE_LABELS_MAP = { devis: "Devis", proforma: "Devis", facture: "Facture", commande: "Commande", avoir: "Avoir", contrat: "Contrat" };

const STATUS_LABELS = { brouillon: "Brouillon", envoye: "Envoyé", envoyee: "Envoyée", accepte: "Accepté", "acompté": "Acompté", "soldé": "Soldé", refuse: "Refusé", expire: "Expiré", resilie: "Résilié", actif: "Actif", confirmee: "Confirmée", en_cours: "En cours", livree: "Livrée", annulee: "Annulée", payee: "Payée", en_retard: "En retard", emis: "Émis", applique: "Appliqué", validee: "Validée", archivee: "Archivée" };
const STATUS_MAP = {
  contrat: ["brouillon", "actif", "expire", "resilie"],
  devis: ["brouillon", "envoye", "accepte", "acompté", "soldé", "refuse", "expire"],
  proforma: ["brouillon", "envoye", "accepte"],
  commande: ["brouillon", "confirmee", "validee", "en_cours", "livree", "annulee"],
  facture: ["brouillon", "emis", "envoyee", "payee", "en_retard", "annulee"],
  avoir: ["brouillon", "emis", "applique"],
};
const PAYMENT_LABELS = { virement: "Virement", cheque: "Chèque", carte: "Carte", especes: "Espèces", prélèvement: "Prélèvement", litige: "Litige", avoir: "Avoir", traite: "Traite", autre: "Autre" };

const MONTHS = [
  { value: 1, label: "Janv" }, { value: 2, label: "Fév" }, { value: 3, label: "Mars" },
  { value: 4, label: "Avr" }, { value: 5, label: "Mai" }, { value: 6, label: "Juin" },
  { value: 7, label: "Juil" }, { value: 8, label: "Août" }, { value: 9, label: "Sept" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Déc" },
];

function getYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= current - 5; y--) years.push(y);
  return years;
}

// Color band system for status indication (replaces text column on mobile)
function getStatusColor(doc) {
  const s = doc.status;
  // Facture-specific logic with is_sent
  if (doc.type === "facture") {
    if (s === "brouillon") return { border: "border-l-amber-400" };
    if (s === "envoyee") return { border: "border-l-orange-500" };
    if (s === "payee" && doc.is_sent) return { border: "border-l-emerald-500" };
    if (s === "payee") return { border: "border-l-blue-500" };
    if (s === "en_retard") return { border: "border-l-red-500" };
    if (s === "annulee" || s === "archivee") return { border: "border-l-slate-400" };
  }
  // All types: universal color groups
  if (s === "brouillon") return { border: "border-l-amber-400" };
  if (["envoye", "envoyee", "emis", "confirmee", "validee", "en_cours"].includes(s)) return { border: "border-l-orange-500" };
  if (["accepte", "payee", "livree", "applique", "actif"].includes(s)) return { border: "border-l-emerald-500" };
  if (["refuse", "en_retard"].includes(s)) return { border: "border-l-red-500" };
  if (["annulee", "expire", "resilie", "archivee"].includes(s)) return { border: "border-l-slate-400" };
  return { border: "border-l-slate-300" };
}

// Legend per document type
const LEGEND_BY_TYPE = {
  facture: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-sky-500", label: "Émis", statusFilter: "emis" },
    { dot: "bg-orange-500", label: "Envoyé(e) non payée", statusFilter: "envoyee" },
    { dot: "bg-emerald-500", label: "Envoyée et payée", statusFilter: "payee_sent" },
    { dot: "bg-blue-500", label: "Payée non envoyée", statusFilter: "payee_not_sent" },
    { dot: "bg-red-500", label: "Impayé(e) / Retard", statusFilter: "en_retard" },
    { dot: "bg-slate-400", label: "Archivé(e)", statusFilter: "archivee" },
  ],
  devis: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-orange-500", label: "Envoyé(e)", statusFilter: "envoye" },
    { dot: "bg-emerald-500", label: "Accepté", statusFilter: "accepte" },
    { dot: "bg-teal-500", label: "Acompté", statusFilter: "acompté" },
    { dot: "bg-blue-500", label: "Soldé", statusFilter: "soldé" },
    { dot: "bg-red-500", label: "Refusé", statusFilter: "refuse" },
    { dot: "bg-slate-400", label: "Archivé(e)", statusFilter: "archivee" },
  ],
  proforma: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-orange-500", label: "Envoyé(e)", statusFilter: "envoye" },
    { dot: "bg-emerald-500", label: "Accepté", statusFilter: "accepte" },
    { dot: "bg-slate-400", label: "Archivé(e)", statusFilter: "archivee" },
  ],
  commande: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-orange-500", label: "Validée / En cours", statusFilter: "confirmee" },
    { dot: "bg-emerald-500", label: "Livrée", statusFilter: "livree" },
    { dot: "bg-slate-400", label: "Annulée", statusFilter: "annulee" },
  ],
  avoir: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-orange-500", label: "Émis", statusFilter: "emis" },
    { dot: "bg-emerald-500", label: "Appliqué", statusFilter: "applique" },
  ],
  contrat: [
    { dot: "bg-amber-400", label: "Brouillon", statusFilter: "brouillon" },
    { dot: "bg-emerald-500", label: "Actif", statusFilter: "actif" },
    { dot: "bg-slate-400", label: "Expiré", statusFilter: "expire" },
    { dot: "bg-red-500", label: "Résilié", statusFilter: "resilie" },
  ],
};

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

function getStatusFilterLabel(status) {
  if (status === "pending") return "Non payées";
  if (status === "payee") return "Payées";
  return status;
}

function getBulkActionLabel(actionKey) {
  if (actionKey === "delete") return "Supprimer";
  if (actionKey === "archive") return "Archiver";
  return "Modifier le statut";
}

function getProformaActionVerb(action) {
  if (action === "pdf") return "imprimer";
  if (action === "email") return "envoyer";
  return "afficher";
}

export default function DocumentsPage({ user }) {
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlType = searchParams.get("type") || "";
  const urlStatus = searchParams.get("status") || "";
  const urlNew = searchParams.get("new");
  const [activeType, setActiveType] = useState(urlType || "devis");
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, docId: "", email: "", cc: "", bcc: "", subject: "", message: "", docNumber: "", print_as: null });
  const [workflow, setWorkflow] = useState(null);
  const [workflowLoaded, setWorkflowLoaded] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companySettings, setCompanySettings] = useState({});
  const [paymentDoc, setPaymentDoc] = useState(null);
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, docId: "", dueDate: "" });
  const [historyDialog, setHistoryDialog] = useState({ open: false, docId: "", docNumber: "" });
  const [pageSize, setPageSize] = useState(50);
  const [proformaChoice, setProformaChoice] = useState({ open: false, action: "", docId: "", number: "", docData: null, docType: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Period filter state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [fullYear, setFullYear] = useState(false);
  const [noPeriodFilter, setNoPeriodFilter] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [periodTotals, setPeriodTotals] = useState({ ht: 0, tva: 0, ttc: 0 });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [acompteDialog, setAcompteDialog] = useState({ open: false, devisId: null });
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const [activeStatus, setActiveStatus] = useState(urlStatus || "");
  useEffect(() => { if (urlType) setActiveType(urlType); if (urlStatus) setActiveStatus(urlStatus); }, [urlType, urlStatus]);

  useEffect(() => {
    adminAPI.getSettings()
      .then(r => { setWorkflow(r.data?.workflow || "devis_facture"); setCompanyName(r.data?.name || ""); setCompanySettings(r.data || {}); })
      .catch(() => setWorkflow("devis_facture"))
      .finally(() => setWorkflowLoaded(true));
  }, []);

  const userModules = user?.modules || [];
  const DOC_TYPES = ALL_DOC_TYPES.filter(dt => dt.workflows.includes(workflow || "devis_facture")).filter(dt => dt.value !== "contrat" || userModules.includes("contrats"));

  useEffect(() => {
    if (!workflowLoaded) return;
    if (urlNew === "1" && urlType) {
      setActiveType(urlType);
      setTimeout(() => {
        setEditing(null);
        setShowDialog(true);
        setSearchParams({}, { replace: true });
      }, 100);
    } else if (DOC_TYPES.length > 0 && !DOC_TYPES.find(dt => dt.value === activeType)) {
      setActiveType(DOC_TYPES[0].value);
    }
  }, [workflowLoaded, urlNew, urlType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref for filter state to keep loadDocs stable
  const filtersRef = useRef({});
  filtersRef.current = { activeType, selectedYear, selectedMonth, fullYear, noPeriodFilter, useCustomDates, customStart, customEnd };

  const loadDocs = useCallback(() => {
    const f = filtersRef.current;
    const params = { type: f.activeType };
    if (!f.noPeriodFilter) {
      if (f.useCustomDates) {
        if (f.customStart) params.date_from = f.customStart;
        if (f.customEnd) params.date_to = f.customEnd;
      } else if (f.fullYear) {
        params.date_from = `${f.selectedYear}-01-01`;
        params.date_to = `${f.selectedYear}-12-31`;
      } else if (f.selectedMonth) {
        const m = String(f.selectedMonth).padStart(2, "0");
        const lastDay = new Date(f.selectedYear, f.selectedMonth, 0).getDate();
        params.date_from = `${f.selectedYear}-${m}-01`;
        params.date_to = `${f.selectedYear}-${m}-${lastDay}`;
      }
    }
    documentAPI.list(params).then(r => {
      setDocs(r.data.items || r.data);
      setPeriodTotals(r.data.totals || { ht: 0, tva: 0, ttc: 0 });
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadRefs = useCallback(() => {
    clientAPI.list({ limit: 5000 }).then(r => setClients(r.data.items || r.data)).catch(() => {});
    productAPI.list({ limit: 5000 }).then(r => setProducts(r.data.items || r.data)).catch(() => {});
  }, []);
  const load = useCallback(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { loadDocs(); }, [activeType, selectedYear, selectedMonth, fullYear, noPeriodFilter, useCustomDates, customStart, customEnd, loadDocs]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const openNew = () => {
    setEditing(null);
    setShowDialog(true);
  };

  const openEdit = (d) => { setEditing(d); setShowDialog(true); };

  const handleClientCreated = (newClient) => {
    setClients(prev => [...prev, newClient]);
  };

  const deleteDoc = async (doc) => {
    const id = typeof doc === "string" ? doc : doc.id;
    const legalNumber = typeof doc === "object" && ["facture", "avoir"].includes(doc.type) && !doc.number_is_temp;
    const msg = legalNumber
      ? `Ce brouillon porte le numéro ${doc.number} de la série légale. En le supprimant, ce numéro sera définitivement perdu (trou dans la numérotation). Continuer ?`
      : "Supprimer ce document ?";
    if (!window.confirm(msg)) return;
    try {
      await documentAPI.delete(id); toast.success("Supprimé"); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    }
  };

  const [emitDialog, setEmitDialog] = useState({ open: false, doc: null, loading: false });

  // Le critère d'émission est le STATUT, jamais le numéro : un brouillon peut porter un
  // numéro de la série légale (documents antérieurs à la numérotation provisoire).
  const isEmitted = (doc) => ["facture", "avoir"].includes(doc.type) && (doc.status || "brouillon") !== "brouillon";
  const isFiscalDraft = (doc) => ["facture", "avoir"].includes(doc.type) && (doc.status || "brouillon") === "brouillon";

  const createAvoir = async (doc) => {
    if (!window.confirm(`Créer un avoir de correction pour la facture ${doc.number} ? Les lignes seront reprises et l'avoir restera en brouillon.`)) return;
    try {
      const res = await convertAPI.convert(doc.id, "avoir");
      toast.success(`Avoir ${res.data.number} créé en brouillon`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création de l'avoir impossible");
    }
  };

  const confirmEmit = async () => {
    const d = emitDialog.doc;
    if (!d) return;
    setEmitDialog(prev => ({ ...prev, loading: true }));
    try {
      const res = await documentAPI.emit(d.id);
      toast.success(`Numéro définitif attribué : ${res.data.number}`);
      setEmitDialog({ open: false, doc: null, loading: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Émission impossible");
      setEmitDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const archiveDoc = async (id) => {
    try {
      await documentAPI.bulkAction([id], "archive");
      toast.success("Document archivé");
      load();
    } catch { toast.error("Erreur archivage"); }
  };

  // La duplication est faite par le serveur : il recopie tout le contenu
  // (conditions de paiement, conditions de vente, acompte, adresse de livraison,
  // references et descriptions de lignes) — une liste de champs tenue ici en
  // perdait huit a chaque copie.
  const duplicateDoc = async (doc) => {
    try {
      const res = await documentAPI.duplicate(doc.id);
      toast.success(`Document dupliqué : ${res.data.number || "OK"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la duplication"); }
  };

  const downloadPdf = async (docId, number, printAs = null) => {
    try {
      const res = await pdfAPI.download(docId, printAs);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${number || docId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur PDF"); }
  };

  const handlePdfClick = (doc) => {
    if (doc.type === "proforma" || doc.type === "commande") {
      setProformaChoice({ open: true, action: "pdf", docId: doc.id, number: doc.number, docData: doc, docType: doc.type });
    } else {
      downloadPdf(doc.id, doc.number);
    }
  };

  const convertDoc = async (docId, targetType) => {
    try {
      const res = await convertAPI.convert(docId, targetType);
      toast.success(`Converti en ${targetType}: ${res.data.number}`);
      load();
      if (targetType === "facture" && res.data?.id) {
        setPreviewDoc(res.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur conversion"); }
  };

  const signDevis = async (docId) => {
    try {
      await documentAPI.signDevis(docId);
      toast.success("Devis marqué comme signé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openAcompteDialog = (devisId) => {
    setAcompteDialog({ open: true, devisId });
  };

  const createSolde = async (docId) => {
    try {
      const res = await documentAPI.createSolde(docId);
      toast.success(`Facture de solde ${res.data.number} créée`);
      load();
      setPreviewDoc(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendEmail = async ({ email, cc, bcc, subject, message }) => {
    if (!email) { toast.error("Email requis"); return; }
    try {
      const res = await pdfAPI.sendEmail(emailDialog.docId, email, {
        cc, bcc, subject, message,
        print_as: emailDialog.print_as || null,
      });
      if (res.data?.status === "error") { toast.error(res.data.message || "Erreur envoi"); return; }
      toast.success(res.data.message || "Email envoyé");
      if (res.data?.warning) toast.warning(res.data.warning, { duration: 12000 });
      setEmailDialog({ open: false, docId: "", email: "", cc: "", bcc: "", subject: "", message: "", docNumber: "", print_as: null });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur envoi"); }
  };

  const buildEmailMessage = (doc, docLabel, clientId) => {
    const client = clients.find(c => c.id === (clientId || doc.client_id));
    const contactName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || client?.contact_name || "Madame, Monsieur";
    const clientCompany = client?.company || "";
    const coName = companyName || "Notre société";
    const docDate = doc.date ? new Date(doc.date).toLocaleDateString("fr-FR") : "";
    const template = companySettings.email_template;
    if (template) {
      const rendered = template
        .replace(/\{client_name\}/g, contactName)
        .replace(/\{doc_label\}/g, docLabel.toLowerCase())
        .replace(/\{doc_number\}/g, doc.number || "")
        .replace(/\{doc_date\}/g, docDate)
        .replace(/\{total_ttc\}/g, formatEur(billedTtc(doc)))
        .replace(/\{reste_a_payer\}/g, invoiceRestePayer(doc))
        .replace(/\{company_name\}/g, coName)
        .replace(/\{client_company\}/g, clientCompany);
      return appendRecapToTemplate(template, rendered, doc);
    }
    const amountBlock = invoiceRecapText(doc) || `Montant TTC : ${formatEur(billedTtc(doc))}`;
    return `Bonjour ${contactName},\n\nVeuillez trouver ci-joint votre ${docLabel.toLowerCase()} ${doc.number} du ${docDate}${clientCompany ? ` pour la société ${clientCompany}` : ""}.\n\n${amountBlock}\n\nNous restons à votre disposition pour toute question.\n\nCordialement,\n${coName}`;
  };

  const handleEmailClick = async (doc, clientEmail) => {
    // Envoyer, c'est émettre : on attribue le numéro définitif AVANT de composer l'email,
    // sinon le sujet et le message porteraient le numéro provisoire.
    if (isFiscalDraft(doc)) {
      const label = DOC_TYPE_LABELS_MAP[doc.type] || "Document";
      const question = doc.number_is_temp
        ? `L'envoi va émettre ce document et remplacer le numéro provisoire ${doc.number} par son numéro définitif. Cette action est irréversible. Continuer ?`
        : `L'envoi va émettre ce document (le numéro ${doc.number} sera conservé). Cette action est irréversible : il ne pourra plus être supprimé ni modifié. Continuer ?`;
      if (!window.confirm(question)) return;
      try {
        const res = await documentAPI.emit(doc.id);
        toast.success(`${label} émis${label === "Facture" ? "e" : ""} : ${res.data.number}`);
        doc = { ...doc, number: res.data.number, number_is_temp: false, status: res.data.status };
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Émission impossible");
        return;
      }
    }
    const client = clients.find(c => c.id === doc.client_id);
    const allEmails = client?.emails || [];
    if (doc.type === "proforma" || doc.type === "commande") {
      setProformaChoice({ open: true, action: "email", docId: doc.id, number: doc.number, docData: doc, clientEmail, clientEmails: allEmails, docType: doc.type });
    } else {
      const docLabel = DOC_TYPE_LABELS_MAP[doc.type] || "Document";
      setEmailDialog({
        open: true, docId: doc.id, email: clientEmail,
        clientEmails: allEmails,
        cc: "", bcc: "", subject: `${docLabel} ${doc.number} - ${companyName}`,
        message: buildEmailMessage(doc, docLabel), docNumber: doc.number, print_as: null,
      });
    }
  };

  const openTaskForDoc = (doc) => {
    const client = clients.find(c => c.id === doc.client_id);
    window.dispatchEvent(new CustomEvent("open-task-panel", { detail: {
      client_id: doc.client_id || "",
      client_name: client?.company || `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || "",
      document_id: doc.id,
      document_number: doc.number,
    }}));
  };

  const confirmProformaChoice = (choice) => {
    const { action, docId, number, docData, clientEmail, clientEmails, docType } = proformaChoice;
    setProformaChoice({ open: false, action: "", docId: "", number: "", docData: null, docType: "" });
    // Proforma choices
    if (docType === "proforma") {
      const printAs = choice === "proforma" ? "facture_proforma" : null;
      const label = choice === "proforma" ? "Facture Proforma" : "Devis";
      if (action === "pdf") {
        downloadPdf(docId, number, printAs);
      } else if (action === "email") {
        setEmailDialog({
          open: true, docId, email: clientEmail || "",
          clientEmails: clientEmails || [],
          cc: "", bcc: "", subject: `${label} ${number} - ${companyName}`,
          message: buildEmailMessage(docData || { number, date: "", total_ttc: 0, client_id: "" }, label, docData?.client_id),
          docNumber: number, print_as: printAs,
        });
      } else if (action === "preview") {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/preview${printAs ? `?print_as=${printAs}` : ""}`;
        window.open(url, "_blank");
      }
    }
    // Commande choices
    if (docType === "commande") {
      if (choice === "delivery_note") {
        if (action === "pdf") {
          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/delivery-note`, '_blank');
        } else if (action === "preview") {
          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/delivery-note-preview`, '_blank');
        }
        // No email for delivery note
      } else {
        // Standard commande
        if (action === "pdf") {
          downloadPdf(docId, number);
        } else if (action === "email") {
          setEmailDialog({
            open: true, docId, email: clientEmail || "",
            clientEmails: clientEmails || [],
            cc: "", bcc: "", subject: `Commande ${number} - ${companyName}`,
            message: buildEmailMessage(docData || { number, date: "", total_ttc: 0, client_id: "" }, "Commande", docData?.client_id),
            docNumber: number, print_as: null,
          });
        } else if (action === "preview") {
          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/preview`, '_blank');
        }
      }
    }
  };


  const getConversionTarget = (doc) => {
    const docType = typeof doc === "string" ? doc : doc?.type;
    const status = typeof doc === "string" ? "" : (doc?.status || "");
    if (workflow === "devis_facture") {
      // Hide the legacy "Convertir en Facture" button when devis is in
      // acompte/solde lifecycle (use the dedicated Acompte/Solde buttons).
      if (docType === "devis" && !["accepte", "acompté", "soldé"].includes(status)) {
        return { target: "facture", label: "Facture" };
      }
    } else {
      if (docType === "proforma") return { target: "commande", label: "Commande" };
      if (docType === "commande") return { target: "facture", label: "Facture" };
    }
    return null;
  };

  // Enfant déjà créé pour la conversion visée : bloque une seconde conversion (point 1 LOT 3).
  const convertedChild = (doc) => {
    const t = getConversionTarget(doc);
    if (!t || !doc?.converted_child) return null;
    return doc.converted_child.type === t.target ? doc.converted_child : null;
  };
  const goToConvertedChild = (child) => {
    if (!child) return;
    setSearch(child.number || "");
    setActiveType(child.type);
    setSearchParams({ type: child.type });
  };
  const convertedChildLabel = (child) => `${DOC_TYPE_LABELS_MAP[child.type] || child.type} ${child.number}`;
  const convertedChildTitle = (child) => `Déjà converti — voir ${(DOC_TYPE_LABELS_MAP[child.type] || child.type).toLowerCase()} ${child.number}`;

  const getClientName = (cid) => { const c = clients.find(cl => cl.id === cid); return c ? (c.trade_name || c.company || `${c.first_name} ${c.last_name}`) : "-"; };

  const pendingStatuses = ["envoyee", "envoye", "brouillon", "emis"];
  const filteredDocs = docs.filter(d => {
    if (activeStatus === "pending" && !pendingStatuses.includes(d.status)) return false;
    if (activeStatus === "payee" && d.status !== "payee") return false;
    if (activeStatus && activeStatus !== "pending" && activeStatus !== "payee" && d.status !== activeStatus) return false;
    if (search && !d.number?.toLowerCase().includes(search.toLowerCase()) && !getClientName(d.client_id).toLowerCase().includes(search.toLowerCase())) return false;
    // Legend status filter
    if (statusFilter) {
      if (!matchesStatusFilter(d, statusFilter)) return false;
    }
    return true;
  });

  // Sort
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "number" || key === "date" ? "desc" : "asc");
    }
  };
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    let va, vb;
    switch (sortKey) {
      case "client":
        va = getClientName(a.client_id).toLowerCase();
        vb = getClientName(b.client_id).toLowerCase();
        break;
      case "date":
        va = a.date || "";
        vb = b.date || "";
        break;
      case "status":
        va = STATUS_LABELS[a.status] || a.status || "";
        vb = STATUS_LABELS[b.status] || b.status || "";
        break;
      case "ttc":
        va = a.total_ttc || 0; vb = b.total_ttc || 0;
        break;
      case "regle":
        va = a.total_paid || 0; vb = b.total_paid || 0;
        break;
      case "payment":
        va = (PAYMENT_LABELS[a.payment_method] || a.payment_method || "").toLowerCase();
        vb = (PAYMENT_LABELS[b.payment_method] || b.payment_method || "").toLowerCase();
        break;
      case "number":
      default:
        va = a.number || ""; vb = b.number || "";
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedDocs.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedDocs = sortedDocs.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [activeType, search, activeStatus, statusFilter]);

  // Sous 640 px, la liste passe en cartes : 20 par page suffisent (50 = plusieurs milliers de px de défilement)
  useEffect(() => {
    if (window.innerWidth < 640) setPageSize(20);
  }, []);

  const selection = useSelection(paginatedDocs);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Clear selection when tab changes
  useEffect(() => { selection.clear(); }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkAction = async (actionKey, params) => {
    if (!selection.selectedIds.length) return;
    const label = { delete: "Supprimer", archive: "Archiver" }[actionKey] || "Modifier le statut";
    if (actionKey === "delete" && !window.confirm(`${label} ${selection.count} document(s) ?`)) return;
    setBulkLoading(true);
    try {
      const res = await documentAPI.bulkAction(selection.selectedIds, actionKey, params);
      toast.success(res.data.message);
      selection.clear();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur action groupée"); }
    finally { setBulkLoading(false); }
  };

  const docBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
    { key: "archive", label: "Archiver", icon: Archive, variant: "outline", className: "text-orange-600 border-orange-200 hover:bg-orange-50" },
    { key: "status", type: "status-select", options: (STATUS_MAP[activeType] || ["brouillon"]).map(s => ({ value: s, label: STATUS_LABELS[s] || s })) },
  ];

  const periodLabel = () => {
    if (noPeriodFilter) return "Toutes les dates";
    if (useCustomDates && customStart && customEnd) return `Du ${new Date(customStart).toLocaleDateString("fr-FR")} au ${new Date(customEnd).toLocaleDateString("fr-FR")}`;
    if (fullYear) return `Année ${selectedYear}`;
    if (selectedMonth) return `${MONTHS.find(m => m.value === selectedMonth)?.label || ""} ${selectedYear}`;
    return `Année ${selectedYear}`;
  };

  // Menu « … » des actions secondaires, partagé par la ligne compacte et la carte mobile
  const renderRowMenu = (d, dtValue, testId) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Autres actions" data-testid={testId}><MoreHorizontal size={16} /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[190px]">
        {isFiscalDraft(d) && (
          <DropdownMenuItem className="text-xs cursor-pointer font-semibold text-emerald-700" onClick={() => setEmitDialog({ open: true, doc: d, loading: false })}><Stamp size={12} className="mr-2" />Émettre</DropdownMenuItem>
        )}
        {!isEmitted(d) && (
          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(d)}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>
        )}
        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setHistoryDialog({ open: true, docId: d.id, docNumber: d.number })}><History size={12} className="mr-2" />Historique d&apos;envoi{(d.send_history?.length || 0) > 0 ? ` (${d.send_history.length})` : ""}</DropdownMenuItem>
        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => duplicateDoc(d)}><Copy size={12} className="mr-2" />Dupliquer</DropdownMenuItem>
        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openTaskForDoc(d)}><CheckSquare size={12} className="mr-2" />Créer une tâche</DropdownMenuItem>
        {dtValue === "facture" && (
          <>
            <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setPaymentDoc(d)}><Banknote size={12} className="mr-2" />Règlements</DropdownMenuItem>
            <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setDueDateDialog({ open: true, docId: d.id, dueDate: d.due_date || "" })}><Calendar size={12} className="mr-2" />Échéance</DropdownMenuItem>
          </>
        )}
        {getConversionTarget(d) && (
          convertedChild(d) ? (
            <DropdownMenuItem className="text-xs cursor-pointer text-slate-500" title={convertedChildTitle(convertedChild(d))} onClick={() => goToConvertedChild(convertedChild(d))} data-testid={`converted-child-menu-${d.id}`}><ArrowRight size={12} className="mr-2" />{convertedChildLabel(convertedChild(d))}</DropdownMenuItem>
          ) : (
            <DropdownMenuItem className="text-xs cursor-pointer text-indigo-600" onClick={() => convertDoc(d.id, getConversionTarget(d).target)} data-testid={`convert-menu-${d.id}`}><ArrowRight size={12} className="mr-2" />{getConversionTarget(d).label}</DropdownMenuItem>
          )
        )}
        {d.type === "devis" && ["brouillon", "envoye", "envoyee"].includes(d.status) && (
          <DropdownMenuItem className="text-xs cursor-pointer text-emerald-700" onClick={() => signDevis(d.id)}><Check size={12} className="mr-2" />Marquer comme signé</DropdownMenuItem>
        )}
        {d.type === "devis" && ["accepte", "acompté"].includes(d.status) && (
          <>
            <DropdownMenuItem className="text-xs cursor-pointer text-violet-700" onClick={() => openAcompteDialog(d.id)}><Banknote size={12} className="mr-2" />Facture d&apos;acompte</DropdownMenuItem>
            <DropdownMenuItem className="text-xs cursor-pointer text-indigo-700" onClick={() => createSolde(d.id)}><Receipt size={12} className="mr-2" />Facture de solde</DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem className="text-xs cursor-pointer text-orange-600" onClick={() => archiveDoc(d.id)}><Archive size={12} className="mr-2" />Archiver</DropdownMenuItem>
        {isEmitted(d) ? (
          <DropdownMenuItem className="text-xs cursor-pointer text-rose-600" onClick={() => createAvoir(d)}><RotateCcw size={12} className="mr-2" />Corriger par un avoir</DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="text-xs cursor-pointer text-red-600" onClick={() => deleteDoc(d)}><Trash2 size={12} className="mr-2" />Supprimer</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="page-container" data-testid="documents-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Documents</h1>
        <p className="text-slate-500 mt-1">{filteredDocs.length} document(s) — {periodLabel()}</p>
      </div>

      {/* Period selector */}
      <PeriodSelector
        noPeriodFilter={noPeriodFilter} setNoPeriodFilter={setNoPeriodFilter}
        selectedYear={selectedYear} setSelectedYear={setSelectedYear}
        fullYear={fullYear} setFullYear={setFullYear}
        selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth}
        useCustomDates={useCustomDates} setUseCustomDates={setUseCustomDates}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
      />

      {/* Period totals */}
      <PeriodTotals periodTotals={periodTotals} periodLabel={periodLabel()} />
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <Tabs value={activeType} onValueChange={(v) => { setActiveType(v); setStatusFilter(null); }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <TabsList className="flex-wrap h-auto">
              {DOC_TYPES.map(dt => (
                <TabsTrigger key={dt.value} value={dt.value} data-testid={`doc-tab-${dt.value}`}>
                  <dt.icon size={14} className="mr-1.5" />{dt.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex items-center gap-2">
              {userModules.includes("export") && (
                <Button variant="outline" onClick={() => {
                  const params = { type: activeType };
                  if (!noPeriodFilter) {
                    if (useCustomDates && customStart) params.date_from = customStart;
                    if (useCustomDates && customEnd) params.date_to = customEnd;
                    if (!useCustomDates && fullYear) { params.date_from = `${selectedYear}-01-01`; params.date_to = `${selectedYear}-12-31`; }
                    if (!useCustomDates && selectedMonth) { const m = String(selectedMonth).padStart(2, "0"); const ld = new Date(selectedYear, selectedMonth, 0).getDate(); params.date_from = `${selectedYear}-${m}-01`; params.date_to = `${selectedYear}-${m}-${ld}`; }
                  }
                  if (activeStatus) params.status = activeStatus;
                  window.open(exportAPI.documentsUrl(params), "_blank");
                  toast.success("Export CSV lancé");
                }} className="uppercase text-xs font-bold tracking-wider" data-testid="export-documents-btn">
                  <Download size={15} className="mr-1.5" /> Exporter
                </Button>
              )}
              <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-document-btn">
                <Plus size={16} className="mr-2" /> Nouveau
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative w-full sm:w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="doc-search" />
              </div>
              {activeStatus && (
                <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer hover:bg-slate-200" onClick={() => { setActiveStatus(""); setSearchParams(prev => { prev.delete("status"); return prev; }); }} data-testid="status-filter-badge">
                  {getStatusFilterLabel(activeStatus)}
                  <X size={12} />
                </Badge>
              )}
            </div>
            <BulkActionBar count={selection.count} actions={docBulkActions} onAction={handleBulkAction} disabled={bulkLoading} />
          </div>
          {DOC_TYPES.map(dt => (
            <TabsContent key={dt.value} value={dt.value}>
              {/* Pagination top */}
              <PaginationBar totalItems={filteredDocs.length} pageSize={pageSize} setPageSize={setPageSize} currentPage={safePage} totalPages={totalPages} setCurrentPage={setCurrentPage} testIdSuffix="-top" />
              {/* Color legend — clickable filters */}
              <StatusLegend legendItems={LEGEND_BY_TYPE[dt.value] || LEGEND_BY_TYPE.devis} docs={docs} statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
              {/* Sous 640 px : cartes empilées (le tableau à 9 colonnes ne tient pas) */}
              <div className="sm:hidden">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="doc-select-all-mobile" />
                  <span className="text-[11px] text-slate-500 flex-1">Tout sélectionner</span>
                  <Select value={sortKey} onValueChange={(v) => setSortKey(v)}>
                    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="doc-sort-select-mobile"><SelectValue placeholder="Trier par" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date" className="text-xs">Date</SelectItem>
                      <SelectItem value="client" className="text-xs">Client</SelectItem>
                      <SelectItem value="status" className="text-xs">Statut</SelectItem>
                      <SelectItem value="ttc" className="text-xs">Total TTC</SelectItem>
                      {dt.value === "facture" && <SelectItem value="regle" className="text-xs">Réglé</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" title={sortDir === "asc" ? "Croissant" : "Décroissant"} onClick={() => setSortDir(prev => prev === "asc" ? "desc" : "asc")} data-testid="doc-sort-dir-mobile">
                    {sortDir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
                  </Button>
                </div>
                <div className="space-y-3" data-testid="doc-cards-mobile">
                  {paginatedDocs.map(d => {
                    const sc = getStatusColor(d);
                    return (
                      <div key={d.id} className={`rounded-lg border border-slate-200 border-l-4 ${sc.border} bg-white p-3 ${selection.selected.has(d.id) ? "bg-indigo-50/60" : ""}`} data-testid={`doc-card-${d.id}`}>
                        <div className="flex items-start gap-2">
                          <Checkbox className="mt-0.5 shrink-0" checked={selection.selected.has(d.id)} onCheckedChange={() => selection.toggle(d.id)} data-testid={`doc-card-select-${d.id}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className={`font-mono text-[13px] font-medium ${d.number_is_temp ? "text-amber-700" : ""}`}
                                title={d.number_is_temp ? "Numéro provisoire : le numéro définitif sera attribué à l'émission" : undefined}
                                data-testid={d.number_is_temp ? `temp-number-card-${d.id}` : undefined}
                              >{d.number}</span>
                              <span className={`status-badge status-${d.status} text-[10px]`}>{STATUS_LABELS[d.status] || d.status}</span>
                            </div>
                            {d.source_doc_number && <div className="text-[10px] text-slate-400 mt-0.5">Ref. {d.source_doc_number}</div>}
                            <DevisMismatchWarning doc={d} suffix="-mobile" />
                            {d.type === "avoir" && (d.status || "brouillon") === "brouillon" && (
                              <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`avoir-not-emitted-mobile-${d.id}`}>
                                Non émis : ne réduit pas encore le solde{d.source_doc_number ? ` de ${d.source_doc_number}` : ""}
                              </div>
                            )}
                            <div className="text-[11px] text-slate-400">{d.date ? new Date(d.date).toLocaleDateString("fr-FR") : ""}</div>
                            <div className="text-[12px] text-indigo-600 truncate mt-0.5 cursor-pointer" onClick={() => navigate(`/clients/${d.client_id}`)} data-testid={`client-link-card-${d.id}`}>{getClientName(d.client_id)}</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-[13px] font-semibold text-slate-900 whitespace-nowrap" title={acompteDeductedTtc(d) > 0 ? `Devis ${formatEur(d.total_ttc)} − acomptes ${formatEur(acompteDeductedTtc(d))}` : undefined} data-testid={`amount-mobile-${d.id}`}>{formatEur(billedTtc(d))}</div>
                            {dt.value === "facture" && ((d.total_paid || 0) > 0 || (d.avoir_imputed_ttc || 0) > 0 || (d.avoir_draft_count || 0) > 0 || (d.avoir_refund_count || 0) > 0) && (
                              <>
                                <div className={`text-[11px] whitespace-nowrap ${(d.remaining ?? 1) <= 0.009 ? "text-emerald-600" : "text-amber-600"}`} data-testid={`paid-amount-mobile-${d.id}`}>Réglé {formatEur(d.total_paid || 0)}</div>
                                {(d.avoir_imputed_ttc || 0) > 0 && <div className="text-[10px] text-rose-600 whitespace-nowrap" data-testid={`avoir-imputed-mobile-${d.id}`}>Avoir −{formatEur(d.avoir_imputed_ttc)}</div>}
                                {(d.remaining ?? 0) > 0.009 && <div className="text-[10px] text-slate-500 whitespace-nowrap" data-testid={`remaining-mobile-${d.id}`}>Reste {formatEur(d.remaining)}</div>}
                                <AvoirPendingNotes doc={d} suffix="-mobile" />
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                          {isFiscalDraft(d) && (
                            <Button variant="outline" size="sm" className="h-8 px-2 text-[11px] text-emerald-700 border-emerald-300" onClick={() => setEmitDialog({ open: true, doc: d, loading: false })} data-testid={`emit-card-${d.id}`}>
                              <Stamp size={12} className="mr-1" />Émettre
                            </Button>
                          )}
                          {dt.value === "facture" && (
                            <Button variant="outline" size="sm" className="h-8 px-2 text-[11px] text-emerald-700 border-emerald-300" onClick={() => setPaymentDoc(d)} data-testid={`payments-card-${d.id}`}>
                              <Banknote size={12} className="mr-1" />Regl.
                            </Button>
                          )}
                          {getConversionTarget(d) && (
                            convertedChild(d) ? (
                              <Button variant="outline" size="sm" className="h-8 px-2 text-[11px] text-slate-500 border-slate-300" title={convertedChildTitle(convertedChild(d))} onClick={() => goToConvertedChild(convertedChild(d))} data-testid={`converted-child-card-${d.id}`}>
                                <ArrowRight size={12} className="mr-1" />{convertedChildLabel(convertedChild(d))}
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm" className="h-8 px-2 text-[11px] text-indigo-600 border-indigo-300" onClick={() => convertDoc(d.id, getConversionTarget(d).target)} data-testid={`convert-card-${d.id}`}>
                                <ArrowRight size={12} className="mr-1" />{getConversionTarget(d).label}
                              </Button>
                            )
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Aperçu" data-testid={`preview-card-${d.id}`} onClick={() => { if (d.type === "proforma" || d.type === "commande") { setProformaChoice({ open: true, action: "preview", docId: d.id, number: d.number, docData: d, docType: d.type }); } else { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/preview`, '_blank'); } }}><Eye size={14} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="PDF" data-testid={`pdf-card-${d.id}`} onClick={() => handlePdfClick(d)}><Download size={14} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Email" data-testid={`email-card-${d.id}`} onClick={() => { const client = clients.find(c => c.id === d.client_id); const clientEmail = client?.email || (client?.emails && client.emails[0]?.value) || ""; handleEmailClick(d, clientEmail); }}><Mail size={14} /></Button>
                          {isEmitted(d) ? (
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Historique d'envoi" data-testid={`history-card-${d.id}`} onClick={() => setHistoryDialog({ open: true, docId: d.id, docNumber: d.number })}>
                              <div className="relative"><History size={14} />{(d.send_history?.length || 0) > 0 && <span className="absolute -top-1 -right-1.5 bg-indigo-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center font-bold">{d.send_history.length}</span>}</div>
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Modifier" data-testid={`edit-card-${d.id}`} onClick={() => openEdit(d)}><Pencil size={14} /></Button>
                          )}
                          {isEmitted(d) && (
                            <Button variant="outline" size="sm" className="h-8 px-2 text-[11px] text-rose-700 border-rose-300" title="Corriger par un avoir" data-testid={`avoir-card-${d.id}`} onClick={() => createAvoir(d)}>
                              <RotateCcw size={12} className="mr-1" />Avoir
                            </Button>
                          )}
                          <div className="ml-auto">{renderRowMenu(d, dt.value, `card-actions-${d.id}`)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {paginatedDocs.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">Aucun document</p>}
                </div>
              </div>
              <ScrollableTable className="responsive-table overflow-x-auto hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10 hidden md:table-cell"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="doc-select-all" /></TableHead>
                      <TableHead className="uppercase text-xs text-slate-500">Numéro</TableHead>
                      <TableHead className="uppercase text-xs text-slate-500 cursor-pointer select-none" onClick={() => toggleSort("client")} data-testid="sort-client">
                        <span className="inline-flex items-center gap-1">Client {sortKey === "client" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                      </TableHead>
                      <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell cursor-pointer select-none" onClick={() => toggleSort("date")} data-testid="sort-date">
                        <span className="inline-flex items-center gap-1">Date {sortKey === "date" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                      </TableHead>
                      <TableHead className="uppercase text-xs text-slate-500 hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="sort-status">
                        <span className="inline-flex items-center gap-1">Statut {sortKey === "status" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                      </TableHead>
                      <TableHead className="uppercase text-xs text-slate-500 text-right cursor-pointer select-none" onClick={() => toggleSort("ttc")} data-testid="sort-ttc">
                        <span className="inline-flex items-center gap-1 justify-end w-full">Total TTC {sortKey === "ttc" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                      </TableHead>
                      {dt.value === "facture" && (
                        <TableHead className="uppercase text-xs text-slate-500 text-right cursor-pointer select-none" onClick={() => toggleSort("regle")} data-testid="sort-regle">
                          <span className="inline-flex items-center gap-1 justify-end w-full">Reste dû {sortKey === "regle" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                        </TableHead>
                      )}
                      <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell cursor-pointer select-none" onClick={() => toggleSort("payment")} data-testid="sort-payment">
                        <span className="inline-flex items-center gap-1">Règlement {sortKey === "payment" && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
                      </TableHead>
                      <TableHead className="uppercase text-xs text-slate-500 w-auto sticky right-0 z-20 bg-white shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.18)]" data-testid="th-actions">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDocs.map(d => {
                      const sc = getStatusColor(d);
                      return (
                      <TableRow key={d.id} className={`table-row-hover border-l-4 ${sc.border} ${selection.selected.has(d.id) ? "bg-indigo-50/50" : ""}`} data-testid={`doc-row-${d.id}`}>
                        <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(d.id)} onCheckedChange={() => selection.toggle(d.id)} data-testid={`doc-select-${d.id}`} /></TableCell>
                        <TableCell className="font-mono text-[12px] font-medium">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={d.number_is_temp ? "text-amber-700 whitespace-nowrap" : "whitespace-nowrap"}
                              title={d.number_is_temp ? "Numéro provisoire : le numéro définitif sera attribué à l'émission" : undefined}
                              data-testid={d.number_is_temp ? `temp-number-${d.id}` : undefined}
                            >{d.number}</span>
                            {d.has_attached_pdf && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/attached-pdf`, "_blank"); }}
                                title="Ouvrir le PDF attaché à ce document"
                                className="text-indigo-500 hover:text-indigo-700"
                                data-testid={`view-attached-pdf-${d.id}`}
                              >
                                <Paperclip size={11} />
                              </button>
                            )}
                            {!d.has_attached_pdf && d.source_doc_has_attached_pdf && d.source_doc_id && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.source_doc_id}/attached-pdf`, "_blank"); }}
                                title={`Ouvrir le PDF attaché au devis source ${d.source_doc_number || ""}`}
                                className="text-slate-400 hover:text-indigo-600"
                                data-testid={`view-source-attached-pdf-${d.id}`}
                              >
                                <Paperclip size={11} />
                              </button>
                            )}
                          </div>
                          {d.source_doc_number && (
                            <div className="text-[10px] text-slate-400 font-normal mt-0.5" data-testid={`source-ref-${d.id}`}>
                              Ref. {d.source_doc_number}
                            </div>
                          )}
                          <DevisMismatchWarning doc={d} />
                          {d.type === "avoir" && (d.status || "brouillon") === "brouillon" && (
                            <div className="text-[10px] text-amber-600 font-normal mt-0.5" data-testid={`avoir-not-emitted-${d.id}`}>
                              Non émis : ne réduit pas encore le solde{d.source_doc_number ? ` de ${d.source_doc_number}` : ""}
                            </div>
                          )}
                          {d.type === "devis" && (d.deposit_invoices?.length > 0 || d.status === "soldé") && (() => {
                            const sumAcomptes = (d.deposit_invoices || []).reduce((s, x) => s + (x.amount_ttc || 0), 0);
                            // Une fois la facture de solde émise (statut « soldé »), il ne reste rien à facturer
                            const isSolde = d.status === "soldé";
                            const remaining = isSolde ? 0 : (d.total_ttc || 0) - sumAcomptes;
                            return (
                              <div className="text-[10px] font-normal mt-0.5 space-y-0.5" data-testid={`acompte-info-${d.id}`}>
                                <div className="text-emerald-600">Acompté : {formatEur(sumAcomptes)}</div>
                                <div className={remaining > 0.01 ? "text-amber-600" : "text-slate-400"}>
                                  {isSolde
                                    ? `Solde facturé : ${formatEur(Math.max(0, (d.total_ttc || 0) - sumAcomptes))}`
                                    : `Solde restant : ${formatEur(Math.max(0, remaining))}`}
                                </div>
                              </div>
                            );
                          })()}
                          <div className="xl:hidden text-[10px] text-slate-400 font-normal">{d.date ? new Date(d.date).toLocaleDateString("fr-FR") : ""}</div>
                        </TableCell>
                        <TableCell className="text-[12px] max-w-[120px] md:max-w-[200px] truncate" title={getClientName(d.client_id)}><span onClick={() => navigate(`/clients/${d.client_id}`)} className="text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer" data-testid={`client-link-${d.id}`}>{getClientName(d.client_id)}</span></TableCell>
                        <TableCell className="text-[12px] hidden xl:table-cell">{d.date ? new Date(d.date).toLocaleDateString("fr-FR") : "-"}</TableCell>
                        <TableCell className="hidden md:table-cell"><span className={`status-badge status-${d.status} text-[10px]`}>{STATUS_LABELS[d.status] || d.status}</span></TableCell>
                        <TableCell className="money-value text-[12px]" data-testid={`amount-${d.id}`}>
                          <span title={acompteDeductedTtc(d) > 0 ? `Devis ${formatEur(d.total_ttc)} − acomptes ${formatEur(acompteDeductedTtc(d))}` : undefined}>{formatEur(billedTtc(d))}</span>
                        </TableCell>
                        {dt.value === "facture" && (
                          <TableCell className="text-right">
                            <ResteDuCell doc={d} />
                          </TableCell>
                        )}
                        <TableCell className="text-[12px] text-slate-600 hidden xl:table-cell">{PAYMENT_LABELS[d.payment_method] || d.payment_method || "-"}</TableCell>
                        <TableCell className="sticky right-0 z-10 bg-white shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.18)]" data-testid={`actions-cell-${d.id}`}>
                          {/* Gestes quotidiens visibles : règlements, aperçu, impression, email */}
                          <div className="hidden md:flex gap-0 items-center flex-nowrap">
                            {dt.value === "facture" && (
                              <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Règlements" onClick={() => setPaymentDoc(d)} data-testid={`payments-doc-${d.id}`}>
                                <Banknote size={11} className="mr-0.5" />Règl.
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Aperçu HTML" data-testid={`preview-doc-${d.id}`} onClick={() => {
                              if (d.type === "proforma" || d.type === "commande") { setProformaChoice({ open: true, action: "preview", docId: d.id, number: d.number, docData: d, docType: d.type }); } else { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/preview`, '_blank'); }
                            }}><Eye size={12} /></Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6" title="Imprimer" data-testid={`print-doc-${d.id}`}><Printer size={12} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[170px]">
                                <DropdownMenuItem className="text-xs cursor-pointer" data-testid={`pdf-doc-${d.id}`} onClick={() => handlePdfClick(d)}><Download size={12} className="mr-2" />Télécharger PDF</DropdownMenuItem>
                                <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => { if (d.type === "proforma" || d.type === "commande") { setProformaChoice({ open: true, action: "preview", docId: d.id, number: d.number, docData: d, docType: d.type }); } else { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/preview`, '_blank'); } }}><Eye size={12} className="mr-2" />Aperçu HTML</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Email" onClick={() => { const client = clients.find(c => c.id === d.client_id); const clientEmail = client?.email || (client?.emails && client.emails[0]?.value) || ""; handleEmailClick(d, clientEmail); }} data-testid={`email-doc-${d.id}`}><Mail size={12} /></Button>

                            {getConversionTarget(d) && (
                              convertedChild(d) ? (
                                <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-slate-500 border-slate-300 hover:bg-slate-50 mr-0.5" title={convertedChildTitle(convertedChild(d))} onClick={() => goToConvertedChild(convertedChild(d))} data-testid={`converted-child-doc-${d.id}`}><ArrowRight size={11} className="mr-0.5" />{convertedChildLabel(convertedChild(d))}</Button>
                              ) : (
                                <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-indigo-600 border-indigo-300 hover:bg-indigo-50" onClick={() => convertDoc(d.id, getConversionTarget(d).target)} data-testid={`convert-doc-${d.id}`}><ArrowRight size={11} className="mr-0.5" />{getConversionTarget(d).label}</Button>
                              )
                            )}
                            {d.type === "devis" && ["brouillon", "envoye", "envoyee"].includes(d.status) && (
                              <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-emerald-600 border-emerald-300 hover:bg-emerald-50" onClick={() => signDevis(d.id)} data-testid={`sign-devis-${d.id}`} title="Marquer comme signé">
                                <Check size={11} className="mr-0.5" />Signé
                              </Button>
                            )}
                            {d.type === "devis" && ["accepte", "acompté"].includes(d.status) && (
                              <>
                                <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-violet-600 border-violet-300 hover:bg-violet-50" onClick={() => openAcompteDialog(d.id)} data-testid={`acompte-devis-${d.id}`} title="Créer une facture d'acompte">
                                  <Banknote size={11} className="mr-0.5" />Acompte
                                </Button>
                                <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-indigo-600 border-indigo-300 hover:bg-indigo-50" onClick={() => createSolde(d.id)} data-testid={`solde-devis-${d.id}`} title="Créer la facture de solde">
                                  <Receipt size={11} className="mr-0.5" />Solde
                                </Button>
                              </>
                            )}
                            {isEmitted(d) && dt.value !== "facture" && (
                              <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-rose-700 border-rose-300 hover:bg-rose-50 mr-0.5" title="Corriger par un avoir" onClick={() => createAvoir(d)} data-testid={`avoir-doc-${d.id}`}>
                                <RotateCcw size={11} className="mr-0.5" />Avoir
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6" title="Autres actions" data-testid={`more-actions-${d.id}`}><MoreHorizontal size={13} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[190px]">
                                {/* Gestes exceptionnels : émettre, modifier, échéance, historique, avoir */}
                                {isFiscalDraft(d) && (
                                  <DropdownMenuItem className="text-xs cursor-pointer text-emerald-700" onClick={() => setEmitDialog({ open: true, doc: d, loading: false })} data-testid={`emit-doc-${d.id}`}><Stamp size={12} className="mr-2" />Émettre</DropdownMenuItem>
                                )}
                                {!isEmitted(d) && (
                                  <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(d)} data-testid={`edit-doc-${d.id}`}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>
                                )}
                                {dt.value === "facture" && (
                                  <DropdownMenuItem className="text-xs cursor-pointer text-orange-700" onClick={() => setDueDateDialog({ open: true, docId: d.id, dueDate: d.due_date || "" })} data-testid={`duedate-doc-${d.id}`}><Calendar size={12} className="mr-2" />Échéance</DropdownMenuItem>
                                )}
                                <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setHistoryDialog({ open: true, docId: d.id, docNumber: d.number })} data-testid={`history-menu-${d.id}`}><History size={12} className="mr-2" />Historique d&apos;envoi{(d.send_history?.length || 0) > 0 ? ` (${d.send_history.length})` : ""}</DropdownMenuItem>
                                <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => duplicateDoc(d)} data-testid={`duplicate-doc-${d.id}`}><Copy size={12} className="mr-2" />Dupliquer</DropdownMenuItem>
                                <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openTaskForDoc(d)} data-testid={`task-menu-${d.id}`}><CheckSquare size={12} className="mr-2" />Créer une tâche</DropdownMenuItem>
                                <DropdownMenuItem className="text-xs cursor-pointer text-orange-600" onClick={() => archiveDoc(d.id)} data-testid={`archive-doc-${d.id}`}><Archive size={12} className="mr-2" />Archiver</DropdownMenuItem>
                                {isEmitted(d) ? (
                                  <DropdownMenuItem className="text-xs cursor-pointer text-rose-600" onClick={() => createAvoir(d)} data-testid={`avoir-menu-${d.id}`}><RotateCcw size={12} className="mr-2" />Corriger par un avoir</DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem className="text-xs cursor-pointer text-red-600" onClick={() => deleteDoc(d)} data-testid={`delete-doc-${d.id}`}><Trash2 size={12} className="mr-2" />Supprimer</DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {/* 640–767 px : menu compact (le tableau reste affiché) */}
                          <div className="md:hidden">
                            {renderRowMenu(d, dt.value, `mobile-actions-${d.id}`)}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {paginatedDocs.length === 0 && <TableRow><TableCell colSpan={dt.value === "facture" ? 9 : 8} className="text-center text-slate-400 py-8">Aucun document</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </ScrollableTable>
              {/* Pagination */}
              <div className="mt-4">
                <PaginationBar totalItems={filteredDocs.length} pageSize={pageSize} setPageSize={setPageSize} currentPage={safePage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Card>
      {/* Document Form Dialog (separate component - isolated re-renders) */}
      <DocumentFormDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={() => { setShowDialog(false); loadDocs(); }}
        editing={editing}
        activeType={activeType}
        clients={clients}
        products={products}
        user={user}
        ClientCombobox={ClientCombobox}
        onNewClient={() => setShowClientDialog(true)}
      />
      {/* Email Dialog */}
      <Dialog open={emitDialog.open} onOpenChange={(o) => !o && setEmitDialog({ open: false, doc: null, loading: false })}>
        <DialogContent className="max-w-md" data-testid="emit-confirm-dialog">
          <DialogHeader><DialogTitle>Émettre la facture</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              {emitDialog.doc?.number_is_temp ? (
                <>Le numéro définitif de la série légale va être attribué à la place du numéro
                provisoire <span className="font-mono font-semibold">{emitDialog.doc?.number}</span>.</>
              ) : (
                <>Ce document va sortir du brouillon et conserver son numéro <span className="font-mono font-semibold">{emitDialog.doc?.number}</span>.</>
              )}
            </p>
            <p className="font-medium text-slate-900">Cette action est irréversible : une facture émise ne peut plus être supprimée ni modifiée, seulement corrigée par un avoir.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEmitDialog({ open: false, doc: null, loading: false })} data-testid="emit-cancel-btn">Annuler</Button>
            <Button onClick={confirmEmit} disabled={emitDialog.loading} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="emit-confirm-btn">
              {emitDialog.loading ? "Émission..." : "Émettre"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmailSendDialog
        open={emailDialog.open}
        onOpenChange={(v) => { if (!v) setEmailDialog(prev => ({ ...prev, open: false })); }}
        initialData={emailDialog}
        onSend={sendEmail}
      />
      {/* Inline Client Creation Dialog */}
      <InlineClientDialog open={showClientDialog} onOpenChange={setShowClientDialog} onCreated={handleClientCreated} />
      {/* Invoice Preview & Send Dialog (after convert devis -> facture) */}
      <InvoicePreviewSendDialog
        open={!!previewDoc}
        onOpenChange={(v) => { if (!v) setPreviewDoc(null); }}
        doc={previewDoc}
        onSent={load}
      />
      <AcompteDialog
        open={acompteDialog.open}
        onOpenChange={(v) => setAcompteDialog((p) => ({ ...p, open: v }))}
        devisId={acompteDialog.devisId}
        onCreated={(doc) => { load(); setPreviewDoc(doc); }}
      />
      {/* Send History Dialog */}
      <SendHistoryDialog
        open={historyDialog.open}
        onOpenChange={(v) => { if (!v) setHistoryDialog({ open: false, docId: "", docNumber: "" }); }}
        docId={historyDialog.docId}
        docNumber={historyDialog.docNumber}
      />
      {/* Payment Dialog */}
      <PaymentDialog
        open={!!paymentDoc}
        onOpenChange={(v) => { if (!v) setPaymentDoc(null); }}
        document={paymentDoc}
        clientName={paymentDoc ? getClientName(paymentDoc.client_id) : ""}
        onUpdated={load}
      />
      {/* Due Date Dialog */}
      <DueDateDialog
        open={dueDateDialog.open}
        onClose={() => setDueDateDialog({ open: false, docId: "", dueDate: "" })}
        docId={dueDateDialog.docId}
        dueDate={dueDateDialog.dueDate}
        setDueDate={v => setDueDateDialog(prev => ({ ...prev, dueDate: v }))}
        onSaved={load}
      />

      {/* Dialog choix format document (Proforma: Devis/Facture Proforma, Commande: Commande/Bon de livraison) */}
      <ProformaChoiceDialog
        open={proformaChoice.open}
        onClose={() => setProformaChoice({ open: false, action: "", docId: "", number: "", docData: null, docType: "" })}
        proformaChoice={proformaChoice}
        onConfirm={confirmProformaChoice}
      />
    </div>
  );
}
